/**
 * @fileoverview Auto-registration page for Click&Pledge with Givent token authentication.
 * Handles automatic user authentication and form setup when a Givent token is available.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';


import { toggleDialog } from '../../../../base/dialog';
import { translate } from '../../../../base/i18n';
import { NOTIFICATION_TIMEOUT, showNotification } from '../../../../notifications';
import {
    bindAccountMode,
    bindChargeDateData,
    bindDonationFormDetails,
    bindGiventID,
    bindIsEventExpired,
    bindMeetFormSettings,
    bindUserAccountData,
    isClickandPledge,
    resetClickandPledge
} from '../../../actions';
import clickPledgeDialog from '../clickPledgeDialog';
import FooterSection from '../sections/FooterSection';
import {
    fetchFormSettingsCached,
    fetchFormWithTokenCached,
    preloadFormData,
    submitRegistrationCached
} from '../utility/apiActions';
import { PAGES } from '../utility/pages';
import { createFormInstance, saveFormGuid } from '../utility/reduxDataUtils';
import { checkRegistrationStatus } from '../utility/registrationUtils';

import AlertMsg from './Alert';

/**
 * Auto-registration page component for Click&Pledge.
 * Automatically authenticates users with Givent tokens and sets up donation forms.
 * Handles the complete registration flow including error handling and retry logic.
 *
 * @param {Object} props - Component props
 * @param {Object} props.meetingData - Meeting information and UUID
 * @param {Object} props.userData - User data including Givent token
 * @param {Function} props.onNavigate - Navigation function to switch pages
 * @param {string} props.serverName - Server name for API calls
 * @param {string} props.versionNumber - Version information
 * @returns {ReactElement} The auto-registration page component
 */
const AutoRegisterPage = ({ t, ...props }) => {
    const [ isRegistering, setIsRegistering ] = useState(true);
    const [ error, setError ] = useState('');
    const [ istablecreated, setIsTableCreated ] = useState(false);
    const isAuthInFlight = useRef(false);
    const hasStartedAuth = useRef(false);
    const hasStartedTableCreate = useRef(false);
    const isMountedRef = useRef(true);
    const streamcheckboxes = useSelector(
        state => state['features/clickandpledge']?.streamOptions || state['features/clickandpledge']?.streamcheckboxes
    );
    const formGuidData = useSelector(state => state['features/clickandpledge']?.formGuidData);
    const formPublishStatus = useSelector(state => state['features/clickandpledge']?.formPublishStatus);
    const reduxState = useMemo(() => {
        return {
            'features/clickandpledge': {
                formGuidData,
                formPublishStatus
            }
        };
    }, [ formGuidData, formPublishStatus ]);
    const dispatch = useDispatch();
    const isEventExpired = useSelector(state => state['features/clickandpledge']?.isEventExpired || '');

    useEffect(() => {
        // Set on every mount (not just via the `useRef(true)` initializer, which
        // runs only once): under React 18 StrictMode the cleanup below fires
        // during the simulated unmount and would otherwise leave the ref stuck at
        // false on the remount, turning every `setErrorSafe`/`setIsRegisteringSafe`
        // into a no-op — which left the loader spinning even after the API
        // finished and swallowed error messages.
        isMountedRef.current = true;

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const setErrorSafe = useCallback(message => {
        if (isMountedRef.current) {
            setError(message);
        }
    }, []);

    const setIsRegisteringSafe = useCallback(value => {
        if (isMountedRef.current) {
            setIsRegistering(value);
        }
    }, []);

    /**
     * Checks if a donation form is already published for this meeting.
     * If published, navigates directly to the payment form; otherwise goes to publish form.
     */
    const checkIfFormPublished = useCallback(async () => {
        try {
            const registrationStatus = await checkRegistrationStatus(props.meetingData, props.userData, reduxState);

            if (registrationStatus.shouldSkipPublish) {
                props.onNavigate(PAGES.LOAD_FORM, { showDForm: true });
            } else {
                props.onNavigate(PAGES.PUBLISH_FORM);
            }
        } catch {

            props.onNavigate(PAGES.PUBLISH_FORM);
        } finally {
            setIsRegisteringSafe(false);
        }
    }, [ props.meetingData, props.userData, props.onNavigate, reduxState, setIsRegisteringSafe ]);

    /**
     * Retrieves form settings from the API and validates event dates.
     * Sets up form configuration and checks if the event is active.
     *
     * @param {string} guid - Form GUID
     * @param {string} giventId - Givent ID for the form
     */
    const getSettings = useCallback(async (guid, giventId) => {
        try {
            const formsettings = await dispatch(fetchFormSettingsCached({
                guid,
                giventId
            }));

            if (formsettings?.forms?.FormSettings) {
                const forms = formsettings.forms;
                const fs = forms.FormSettings || {};

                dispatch(bindMeetFormSettings(fs));
                dispatch(bindChargeDateData(forms.ChargeDate));
                dispatch(bindAccountMode({
                    Mode: forms.Mode,
                    accountStatus: forms.Active
                }));

                const startDate = fs.MeetingStartDate ? new Date(fs.MeetingStartDate) : null;
                const endDate = fs.MeetingEndDate ? new Date(fs.MeetingEndDate) : null;
                const today = new Date();

                today.setHours(0, 0, 0, 0);

                if (startDate && startDate > today) {
                    dispatch(bindIsEventExpired(t('clickandpledge.eventNotStarted')));
                    setIsRegisteringSafe(false);
                } else if (endDate && endDate < today) {
                    setIsRegisteringSafe(false);
                    dispatch(bindIsEventExpired(t('clickandpledge.eventExpired')));
                } else {
                    // Check if form is already published, if so go directly to payment form
                    await checkIfFormPublished();
                }
            } else {
                setErrorSafe(t('clickandpledge.loadFormSettingsError'));
                setIsRegisteringSafe(false);
            }
        } catch {
            setErrorSafe(t('clickandpledge.loadFormSettingsError'));
            setIsRegisteringSafe(false);
        }
    }, [ dispatch, props.onNavigate, props.meetingData, checkIfFormPublished, t, setErrorSafe, setIsRegisteringSafe ]);

    /**
     * Authenticates the user with their Givent token and sets up the donation form.
     * Handles the complete registration process including API calls and error handling.
     */
    const authenticateUser = useCallback(async () => {
        if (isAuthInFlight.current) {
            return;
        }

        isAuthInFlight.current = true;

        try {
            const postbody = {
                HostID: props.userData.hostId,
                HostName: props.userData.display_name,
                HostContactName: '',
                HostAcctID: props.userData.accountId,
                HostUserID: props.userData.userId,
                HostEmail: props.userData.email,
                MeetingID: props.userData.meetingId,
                MeetingStartTime: props.userData.startTime,
                Platform: 'meethour',
                GiventToken: props.userData.givenToken
            };

            const registerResponse = await dispatch(submitRegistrationCached({ postbody }));

            if (registerResponse?.statusCode === 400) {
                const errorMsg = t('clickandpledge.registrationFailedCredentials');

                setErrorSafe(errorMsg);
                setIsRegisteringSafe(false);
                dispatch(showNotification({
                    appearance: 'error',
                    description: errorMsg,
                    titleKey: 'clickandpledge.registrationFailed'
                }, NOTIFICATION_TIMEOUT));

                return false;
            }

            if (registerResponse?.body?.forms?.Error) {
                const errorMsg = registerResponse.body.forms.Error;

                setErrorSafe(errorMsg);
                setIsRegisteringSafe(false);
                dispatch(showNotification({
                    appearance: 'error',
                    description: errorMsg,
                    titleKey: 'clickandpledge.registrationFailed'
                }, NOTIFICATION_TIMEOUT));

                return false;
            }

            if (registerResponse?.body?.forms?.message === 'success') {


                const streamformattedData = Object.entries(streamcheckboxes || {})
                    .map(([ key, value ]) => `${key}-${value}`)
                    .join(';');
                const formdata = await dispatch(fetchFormWithTokenCached({
                    accountId: props.userData.accountId,
                    giventToken: props.userData.givenToken,
                    streamformattedData
                }));

                if (!formdata || formdata === 'Error occured.' || formdata?.code) {
                    const errorMsg = formdata?.code || t('clickandpledge.invalidGiventToken');

                    setErrorSafe(errorMsg);
                    setIsRegisteringSafe(false);
                    dispatch(showNotification({
                        appearance: 'error',
                        description: errorMsg,
                        titleKey: 'clickandpledge.loadFormError'
                    }, NOTIFICATION_TIMEOUT));

                    return false;
                }

                if (formdata.forms?.Error) {
                    const errorMsg = formdata.forms.Error;

                    setErrorSafe(errorMsg);
                    setIsRegisteringSafe(false);
                    dispatch(showNotification({
                        appearance: 'error',
                        description: errorMsg,
                        titleKey: 'clickandpledge.loadFormError'
                    }, NOTIFICATION_TIMEOUT));

                    return false;
                }

                if (formdata.forms?.OrganizationName) {
                    dispatch(bindDonationFormDetails({
                        orgName: formdata.forms.OrganizationName,
                        campaignName: formdata.forms.CampaignName,
                        formName: formdata.forms.FormName,
                        formguid: formdata.forms.FormGuid
                    }));

                    const saveforminfo = {
                        eId: props.meetingData.meetingUUID,
                        guid: formdata.forms.FormGuid,
                        tableName: props.meetingData.meetingUUID,
                        formname: formdata.forms.FormName,
                        orgname: formdata.forms.OrganizationName,
                        campaignname: formdata.forms.CampaignName,
                        giventId: formdata.forms.GiventID.toString()
                    };

                    const formres = await saveFormGuid(dispatch, saveforminfo);

                    if (formres.success) {
                        const guid = saveforminfo.guid;
                        const recordId = `record_${Date.now()}`;


                        dispatch(bindUserAccountData({
                            recordId,
                            formGUID: guid
                        }));
                        dispatch(bindGiventID(formdata.forms.GiventID));

                        // Preload campaign and form data for faster page loads (non-critical)
                        preloadFormData({
                            guid: formdata.forms.FormGuid,
                            giventId: formdata.forms.GiventID
                        })(dispatch).catch(() => undefined);

                        await getSettings(formdata.forms.FormGuid, formdata.forms.GiventID);
                    }
                } else {
                    setErrorSafe(t('clickandpledge.loadFormSettingsError'));
                    setIsRegisteringSafe(false);
                }
            } else {
                setErrorSafe(t('clickandpledge.registrationNoSuccess'));
                setIsRegisteringSafe(false);
            }
        } catch (registrationError) {
            setErrorSafe(registrationError.response?.data?.message || registrationError.message || t('clickandpledge.registrationFailed'));
            setIsRegisteringSafe(false);
        } finally {
            isAuthInFlight.current = false;
        }
    }, [ props.userData, props.meetingData, streamcheckboxes, dispatch, getSettings, t, setErrorSafe, setIsRegisteringSafe ]);

    // Create table instance.
    // The per-instance `hasStartedTableCreate` ref dedupes within a single mount
    // (including React StrictMode's mount→unmount→remount, where refs persist on
    // the same instance), while a genuine remount gets a fresh ref and re-runs.
    // Duplicate network calls are additionally collapsed by the in-flight dedup
    // in apiActions, so no module-level cross-instance guard is needed.
    useEffect(() => {
        if (props.meetingData && props.userData && !istablecreated) {
            if (hasStartedTableCreate.current) {
                return;
            }

            hasStartedTableCreate.current = true;
            const instancebody = { tableName: props.meetingData.meetingUUID };

            createFormInstance(dispatch, instancebody)
                .then(() => setIsTableCreated(true))
                .catch(() => {
                    // Ignore table creation errors for now; allow a retry.
                    hasStartedTableCreate.current = false;
                });
        }
    }, [ props.meetingData, props.userData, istablecreated, dispatch ]);

    // Auto-register when table is created
    useEffect(() => {
        if (istablecreated && props.userData?.givenToken && isRegistering) {
            if (hasStartedAuth.current) {
                return;
            }

            hasStartedAuth.current = true;
            authenticateUser();
        }
    }, [ istablecreated, props.userData?.givenToken, isRegistering, authenticateUser ]);

    /**
     * Retries the registration process after an error.
     * Resets error state and attempts authentication again.
     */
    const retryRegistration = () => {
        hasStartedAuth.current = false;
        hasStartedTableCreate.current = false;
        setErrorSafe('');
        dispatch(resetClickandPledge());
        dispatch(toggleDialog(clickPledgeDialog));
        dispatch(isClickandPledge(false));
    };

    return (
        <div className = 'recurring_transaction_container'>
            <div className = 'recurring_transaction_wrapper'>
                <div
                    className = 'recurring_transaction_card'
                    id = 'recurring_main_tabs'>
                    <div className = 'recurring_transaction_card_body mt_2'>
                        <div className = 'recurring_transaction_main_form'>
                            <div className = 'recurring_transaction_tab_content active'>
                                <div className = 'transaction_info_wrapper'>
                                    {isRegistering && !error && (
                                        <div className = 'loader-container'>
                                            <div className = 'lds-ellipsis'>
                                                <div />
                                                <div />
                                                <div />
                                                <div />
                                            </div>
                                            <p className = 'auth-message'>
                                                {t('clickandpledge.authenticatingWithGiventToken')}
                                            </p>
                                        </div>
                                    )}
                                    <div className = 'transaction_info_wrapper_error'>
                                        {error && (
                                            <AlertMsg
                                                isClose = { false }
                                                message = { error }
                                                type = 'failure' />
                                        )}
                                        {isEventExpired && (
                                            <AlertMsg
                                                isClose = { false }
                                                message = { isEventExpired }
                                                type = 'failure' />
                                        )}
                                        {error && !isRegistering && (
                                            <div className = 'form_input_wrapper mt_2'>
                                                <button
                                                    className = 'full_button'
                                                    onClick = { retryRegistration }
                                                    type = 'button'>
                                                    {t('clickandpledge.retryAuthentication')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <FooterSection />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default translate(AutoRegisterPage);
